import React, { useState, useEffect, useRef, useMemo } from 'react'
import { toTitleCase } from '../../utils/textFormatting'
import './ActivityStream.scss'

const STATE_LABELS: Record<string, string> = {
  'imported': 'Job Imported',
  'analyzing': 'Analysis Started',
  'analyzed': 'Job Analyzed',
  'resume_created': 'Resume Created',
  'resume_generated': 'Resume Tailor-Made', // Map resume_generated to Tailor-Made
  'tailor_made': 'Resume Tailor-Made',
  'applied': 'Application Submitted',
  'offer_received': 'Offer Received',
  'rejected': 'Application Rejected',
  'no_offer': 'No Offer Received',
  'offer_accepted': 'Offer Accepted',
  'offer_rejected': 'Offer Rejected',
  'deleted': 'Job Deleted',
  'analysis_completed': 'Analysis Completed',
  'question_reviewed': 'Reviewed a question'
}

const STATE_ICONS: Record<string, string> = {
  'imported': '📥',
  'analyzing': '🔍',
  'analyzed': '✅',
  'resume_created': '📄',
  'resume_generated': '📄',
  'tailor_made': '📄',
  'applied': '✉️',
  'offer_received': '✅',
  'rejected': '❌',
  'no_offer': '❌',
  'offer_accepted': '✅',
  'offer_rejected': '⚠️',
  'deleted': '🗑️',
  'analysis_completed': '✅',
  'question_reviewed': '📚'
}

const STATE_COLORS: Record<string, string> = {
  'imported': '#505fe2',
  'analyzing': '#17a2b8',
  'analyzed': '#28a745',
  'resume_created': '#28a745',
  'resume_generated': '#28a745',
  'tailor_made': '#28a745',
  'applied': '#ffc107',
  'offer_received': '#28a745',
  'rejected': '#dc3545',
  'no_offer': '#dc3545',
  'offer_accepted': '#28a745',
  'offer_rejected': '#ffc107',
  'deleted': '#6c757d',
  'analysis_completed': '#28a745',
  'question_reviewed': '#505fe2'
}

interface Activity {
  id?: string
  jobId?: string
  jobTitle?: string
  company?: string
  state: string
  timestamp: string | number
}

interface ActivityStreamProps {
  activities?: Activity[]
}

const ITEMS_PER_PAGE = 20

function ActivityStream({ activities = [] }: ActivityStreamProps) {
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isLoadingMoreRef = useRef(false)

  // Filter out activities with invalid timestamps
  const validActivities = useMemo(() => {
    if (!activities || activities.length === 0) {
      return []
    }

    return activities.filter(activity => {
      if (!activity.timestamp) return false
      
      // Parse the timestamp (could be ISO string or Unix timestamp)
      let date: Date
      if (typeof activity.timestamp === 'string') {
        date = new Date(activity.timestamp)
      } else if (typeof activity.timestamp === 'number') {
        // If it's a Unix timestamp, check if it's in seconds or milliseconds
        date = activity.timestamp < 10000000000 
          ? new Date(activity.timestamp * 1000) // seconds
          : new Date(activity.timestamp) // milliseconds
      } else {
        return false
      }
      
      // Skip invalid dates
      return !isNaN(date.getTime())
    })
  }, [activities])

  // Reset displayed count when activities change
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE)
  }, [activities.length])

  // Get activities to display (for infinite scroll)
  const displayedActivities = useMemo(() => {
    return validActivities.slice(0, displayedCount)
  }, [validActivities, displayedCount])

  // Handle scroll to load more
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      // Check if we're near the bottom (within 100px)
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight

      // Load more if we're near the bottom and not already loading
      if (distanceFromBottom < 100 && !isLoadingMoreRef.current && displayedCount < validActivities.length) {
        isLoadingMoreRef.current = true
        // Load more items
        setDisplayedCount(prev => Math.min(prev + ITEMS_PER_PAGE, validActivities.length))
        // Reset loading flag after a short delay
        setTimeout(() => {
          isLoadingMoreRef.current = false
        }, 100)
      }
    }

    container.addEventListener('scroll', handleScroll)
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [displayedCount, validActivities.length])

  const formatDate = (timestamp: string | number): string => {
    // Parse the timestamp (could be ISO string or Unix timestamp)
    let date: Date
    if (typeof timestamp === 'string') {
      date = new Date(timestamp)
    } else if (typeof timestamp === 'number') {
      // If it's a Unix timestamp, check if it's in seconds or milliseconds
      date = timestamp < 10000000000 
        ? new Date(timestamp * 1000) // seconds
        : new Date(timestamp) // milliseconds
    } else {
      date = new Date()
    }
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit'
    } as Intl.DateTimeFormatOptions)
  }

  if (validActivities.length === 0) {
    return (
      <div className="activity-stream-empty">
        <div className="empty-icon">📋</div>
        <p className="empty-text">No activity yet. Import your first job posting to get started!</p>
      </div>
    )
  }

  return (
    <div className="activity-stream activity-stream-container" ref={scrollContainerRef}>
      <div className="activity-timeline">
        {displayedActivities.map((activity, index) => {
          const stateLabel = STATE_LABELS[activity.state] || activity.state
          const stateIcon = STATE_ICONS[activity.state] || '📌'
          const stateColor = STATE_COLORS[activity.state] || '#606064'

          return (
            <div key={activity.id || index} className="activity-item">
              <div className="activity-indicator" style={{ backgroundColor: stateColor }}>
                <span className="activity-icon">{stateIcon}</span>
              </div>
              <div className="activity-content">
                <div className="activity-job-title">{toTitleCase(activity.jobTitle) || 'Unknown Job'}</div>
                {activity.company && (
                  <div className="activity-company">{activity.company}</div>
                )}
                <div className="activity-state">
                  {stateLabel}
                </div>
                <div className="activity-time">{formatDate(activity.timestamp)}</div>
              </div>
            </div>
          )
        })}
      </div>
      {displayedCount < validActivities.length && (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading more...</span>
          </div>
          <p className="text-muted small mt-2 mb-0">Loading more activities...</p>
        </div>
      )}
    </div>
  )
}

export default ActivityStream
